import { useState } from 'react';
import { X, Search, BookOpen, Video, MessageCircle, ChevronRight, ExternalLink } from 'lucide-react';

interface HelpCenterProps {
  isOpen: boolean;
  onClose: () => void;
}

interface Article {
  title: string;
  category: string;
  excerpt: string;
}

const ARTICLES: Article[] = [
  { title: 'Getting Started with Your First Search', category: 'Basics', excerpt: 'Learn how to search parcels by county, city, and state with filters.' },
  { title: 'Understanding Investment Score', category: 'Scoring', excerpt: 'How our algorithm rates parcels for land investment potential.' },
  { title: 'Using the Interactive Heat Map', category: 'Maps', excerpt: 'Visualize parcel density and scores on an interactive map.' },
  { title: 'Managing Your Pipeline', category: 'CRM', excerpt: 'Track parcels from new lead through to closed deal.' },
  { title: 'Commission Calculator Guide', category: 'Finance', excerpt: 'Calculate potential commission on each parcel.' },
  { title: 'Exporting Data (CSV & PDF)', category: 'Data', excerpt: 'Export parcel data for offline analysis and reports.' },
  { title: 'Setting Up County Watchlists', category: 'Alerts', excerpt: 'Get notified when new high-scoring parcels appear.' },
  { title: 'Team Management & Invites', category: 'Team', excerpt: 'Add team members and manage permissions.' },
  { title: 'API Access & Documentation', category: 'Developers', excerpt: 'Integrate Parcel Lead Pro into your existing tools.' },
  { title: 'Understanding Tax Delinquent Data', category: 'Data', excerpt: 'How to find and evaluate tax delinquent parcels.' },
  { title: 'Bulk Operations Guide', category: 'Advanced', excerpt: 'Select, compare, and export multiple parcels at once.' },
  { title: 'Account Settings & Billing', category: 'Account', excerpt: 'Manage your subscription, password, and preferences.' },
];

const CATEGORIES = ['All', 'Basics', 'Scoring', 'Maps', 'CRM', 'Finance', 'Data', 'Alerts', 'Team', 'Developers', 'Advanced', 'Account'];

export default function HelpCenter({ isOpen, onClose }: HelpCenterProps) {
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [selectedArticle, setSelectedArticle] = useState<Article | null>(null);

  const filtered = ARTICLES.filter((a) => {
    const matchesSearch = a.title.toLowerCase().includes(search.toLowerCase()) || a.excerpt.toLowerCase().includes(search.toLowerCase());
    const matchesCategory = activeCategory === 'All' || a.category === activeCategory;
    return matchesSearch && matchesCategory;
  });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" style={{ background: 'rgba(10,15,20,0.85)', backdropFilter: 'blur(8px)' }}>
      <div className="relative w-full max-w-2xl rounded-xl border overflow-hidden flex flex-col" style={{ background: '#232F3E', borderColor: 'rgba(247,248,244,0.08)', maxHeight: '85vh' }}>
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b shrink-0" style={{ borderColor: 'rgba(247,248,244,0.08)' }}>
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg flex items-center justify-center" style={{ background: 'rgba(59,130,246,0.15)' }}>
              <BookOpen size={18} style={{ color: '#3b82f6' }} />
            </div>
            <div>
              <h3 className="text-base font-semibold" style={{ color: '#F7F8F4' }}>Help Center</h3>
              <p className="text-xs" style={{ color: '#A8B8AE' }}>{ARTICLES.length} articles</p>
            </div>
          </div>
          <button
            onClick={onClose}
            data-shortcut="close"
            aria-label="Close help center"
            className="h-8 w-8 rounded-lg flex items-center justify-center transition-colors"
            style={{ color: '#A8B8AE' }}
            onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(247,248,244,0.08)')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
          >
            <X size={16} />
          </button>
        </div>

        {/* Search */}
        <div className="p-4 border-b shrink-0" style={{ borderColor: 'rgba(247,248,244,0.08)' }}>
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'rgba(255,255,255,0.3)' }} />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-shortcut="search"
              className="w-full rounded-lg border pl-9 pr-3 py-2.5 text-sm outline-none transition-colors focus:border-[#7CFF6B]"
              style={{ background: '#07110D', borderColor: 'rgba(247,248,244,0.12)', color: '#F7F8F4' }}
              placeholder="Search help articles..."
            />
          </div>
        </div>

        {/* Categories */}
        <div className="flex gap-1.5 p-3 overflow-x-auto border-b shrink-0" style={{ borderColor: 'rgba(247,248,244,0.08)' }}>
          {CATEGORIES.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className="px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-all"
              style={{
                background: activeCategory === cat ? 'rgba(186,255,41,0.15)' : '#07110D',
                color: activeCategory === cat ? '#7CFF6B' : '#A8B8AE',
              }}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto custom-scrollbar p-4">
          {selectedArticle ? (
            <div className="space-y-4">
              <button
                onClick={() => setSelectedArticle(null)}
                className="text-xs flex items-center gap-1 transition-colors hover:opacity-80"
                style={{ color: '#7CFF6B' }}
              >
                <ChevronRight size={12} className="rotate-180" /> Back to articles
              </button>
              <h4 className="text-lg font-semibold" style={{ color: '#F7F8F4' }}>{selectedArticle.title}</h4>
              <span className="inline-block px-2 py-0.5 rounded text-[10px] font-medium" style={{ background: 'rgba(186,255,41,0.1)', color: '#7CFF6B' }}>
                {selectedArticle.category}
              </span>
              <div className="space-y-3 text-sm leading-relaxed" style={{ color: 'rgba(245,245,243,0.7)' }}>
                <p>{selectedArticle.excerpt}</p>
                <p>
                  This article provides detailed guidance on {selectedArticle.title.toLowerCase()}.
                  For step-by-step instructions, please refer to the related video tutorials or
                  contact our support team for personalized assistance.
                </p>
                <p>
                  If you need additional help, our customer support team is available Monday through
                  Friday, 9 AM to 6 PM EST. You can reach us via the support chat or email at
                  support@parcleadpro.com.
                </p>
              </div>
              <div className="flex gap-3 pt-4">
                <button className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs transition-colors border" style={{ background: '#07110D', borderColor: 'rgba(247,248,244,0.12)', color: '#F7F8F4' }}>
                  <Video size={14} /> Watch Video
                </button>
                <button className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs transition-colors border" style={{ background: '#07110D', borderColor: 'rgba(247,248,244,0.12)', color: '#F7F8F4' }}>
                  <ExternalLink size={14} /> Full Guide
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {filtered.map((article, i) => (
                <button
                  key={i}
                  onClick={() => setSelectedArticle(article)}
                  className="w-full text-left rounded-lg border p-3 transition-all group"
                  style={{ background: '#07110D', borderColor: 'rgba(255,255,255,0.06)' }}
                  onMouseEnter={(e) => (e.currentTarget.style.borderColor = 'rgba(186,255,41,0.3)')}
                  onMouseLeave={(e) => (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)')}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate" style={{ color: '#F7F8F4' }}>{article.title}</p>
                      <p className="text-xs mt-0.5 truncate" style={{ color: '#A8B8AE' }}>{article.excerpt}</p>
                    </div>
                    <div className="flex items-center gap-2 ml-3 shrink-0">
                      <span className="px-2 py-0.5 rounded text-[10px]" style={{ background: 'rgba(255,255,255,0.06)', color: '#A8B8AE' }}>
                        {article.category}
                      </span>
                      <ChevronRight size={14} className="transition-transform group-hover:translate-x-0..5" style={{ color: 'rgba(255,255,255,0.3)' }} />
                    </div>
                  </div>
                </button>
              ))}
              {filtered.length === 0 && (
                <div className="text-center py-8">
                  <Search size={24} className="mx-auto mb-2" style={{ color: 'rgba(255,255,255,0.2)' }} />
                  <p className="text-sm" style={{ color: 'rgba(245,245,243,0.4)' }}>No articles found</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t shrink-0" style={{ borderColor: 'rgba(247,248,244,0.08)' }}>
          <div className="flex items-center justify-between">
            <p className="text-xs" style={{ color: 'rgba(245,245,243,0.4)' }}>
              Still need help? Contact our support team
            </p>
            <button
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-[#F7F8F4] transition-all hover:opacity-90"
              style={{ background: '#7CFF6B' }}
            >
              <MessageCircle size={12} />
              Contact Support
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
